/**
 * Options for merging and ordering color arrays from multiple sources
 */
export interface ColorMergeOptions {
  /**
   * BinDoc brand colors - always prioritized for brand consistency
   */
  brandColors?: string[];

  /**
   * Existing default colors from the system
   */
  existingColors?: string[];

  /**
   * User's color history (recently used colors)
   */
  historyColors?: string[];

  /**
   * External colors (e.g., from current dashboard context)
   */
  externalColors?: string[];

  /**
   * Maximum number of colors to return
   * @default 20
   */
  maxColors?: number;

  /**
   * Strategy for ordering colors
   * - 'recent-first': History colors appear first, then brand colors
   * - 'brand-first': Brand colors appear first, then history colors
   * @default 'recent-first'
   */
  orderStrategy?: 'recent-first' | 'brand-first';
}

/**
 * Color entry with metadata for ordering
 */
interface ColorEntry {
  hex: string;
  source: 'brand' | 'history' | 'external' | 'existing';
  priority: number;
}

/**
 * Normalizes a hex color string to uppercase format
 * @param color - Hex color string (with or without #)
 * @returns Normalized uppercase hex string with #
 */
function normalizeHexColor(color: string): string {
  const hex = color.trim().toUpperCase();
  return hex.startsWith('#') ? hex : `#${hex}`;
}

/**
 * Creates color entries with priority based on source and strategy
 */
function createColorEntries(
  colors: string[],
  source: ColorEntry['source'],
  basePriority: number
): ColorEntry[] {
  return colors.map((hex, index) => ({
    hex: normalizeHexColor(hex),
    source,
    priority: basePriority - index, // Earlier items get higher priority
  }));
}

/**
 * Merges and orders colors from multiple sources with intelligent deduplication
 *
 * This function combines colors from brand palette, user history, external sources,
 * and existing defaults into a single, deduplicated, intelligently ordered array.
 *
 * @param options - Configuration options for merging colors
 * @returns Array of unique hex color strings, ordered by priority
 *
 * @example
 * ```typescript
 * const colors = mergeAndOrderColors({
 *   brandColors: ['#A7206C', '#3F9846'],
 *   historyColors: ['#FF0000', '#A7206C'], // Duplicate will be removed
 *   externalColors: ['#00FF00'],
 *   maxColors: 10,
 *   orderStrategy: 'recent-first'
 * });
 * // Returns: ['#FF0000', '#A7206C', '#3F9846', '#00FF00', ...]
 * ```
 */
export function mergeAndOrderColors(options: ColorMergeOptions): string[] {
  const {
    brandColors = [],
    existingColors = [],
    historyColors = [],
    externalColors = [],
    maxColors = 20,
    orderStrategy = 'recent-first',
  } = options;

  // Priority values based on strategy
  const priorities = {
    'recent-first': {
      history: 1000,
      brand: 900,
      external: 800,
      existing: 700,
    },
    'brand-first': {
      brand: 1000,
      history: 900,
      external: 800,
      existing: 700,
    },
  };

  const strategyPriorities = priorities[orderStrategy];

  // Create entries for each color source
  const allEntries: ColorEntry[] = [
    ...createColorEntries(historyColors, 'history', strategyPriorities.history),
    ...createColorEntries(brandColors, 'brand', strategyPriorities.brand),
    ...createColorEntries(externalColors, 'external', strategyPriorities.external),
    ...createColorEntries(existingColors, 'existing', strategyPriorities.existing),
  ];

  // Deduplicate by hex value, keeping the highest priority entry
  const colorMap = new Map<string, ColorEntry>();

  for (const entry of allEntries) {
    const existing = colorMap.get(entry.hex);
    if (!existing || entry.priority > existing.priority) {
      colorMap.set(entry.hex, entry);
    }
  }

  // Sort by priority (descending) and extract hex values
  const sortedColors = Array.from(colorMap.values())
    .sort((a, b) => b.priority - a.priority)
    .map(entry => entry.hex)
    .slice(0, maxColors);

  return sortedColors;
}
